import styled from '@emotion/styled';

/* -----------------------------------------------------------------------------
 * Compact "Required Documents" card. Visually mirrors the Client Engagement
 * card (radial gradient + soft purple frame + shadow) so the two surfaces
 * read as a matched pair when stacked under it.
 *
 * The card owns the document actions inline: preview, upload/replace, and
 * attach-to-case.
 * ---------------------------------------------------------------------------*/

export const Card = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px 16px;
    border: 1px solid rgba(131, 83, 226, 0.1);
    border-radius: 20px;
    background:
        radial-gradient(circle at top right, rgba(131, 83, 226, 0.08), transparent 34%),
        linear-gradient(180deg, #ffffff 0%, #fcfbff 100%);
    box-shadow:
        0 10px 24px -14px rgba(15, 23, 42, 0.18),
        0 1px 3px rgba(15, 23, 42, 0.04);
    width: 100%;
    position: relative;
    z-index: 1;
    transition:
        transform 0.2s ease,
        box-shadow 0.2s ease,
        border-color 0.2s ease;

    &:hover {
        transform: translateY(-1px);
        border-color: rgba(131, 83, 226, 0.28);
        box-shadow:
            0 14px 28px -16px rgba(15, 23, 42, 0.2),
            0 1px 3px rgba(15, 23, 42, 0.05);
    }

    &:focus-visible {
        outline: none;
        border-color: rgba(131, 83, 226, 0.45);
        box-shadow:
            0 0 0 3px rgba(131, 83, 226, 0.18),
            0 10px 24px -14px rgba(15, 23, 42, 0.18);
    }
`;

/** Same surface as `Card` but never interactive — used for loading/error states. */
export const CardStatic = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px 16px;
    border: 1px solid rgba(131, 83, 226, 0.1);
    border-radius: 20px;
    background:
        radial-gradient(circle at top right, rgba(131, 83, 226, 0.08), transparent 34%),
        linear-gradient(180deg, #ffffff 0%, #fcfbff 100%);
    box-shadow:
        0 10px 24px -14px rgba(15, 23, 42, 0.18),
        0 1px 3px rgba(15, 23, 42, 0.04);
    width: 100%;
    position: relative;
    z-index: 1;
`;

export const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

export const HeaderRight = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
`;

export const Eyebrow = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 4px 8px;
    border-radius: 999px;
    background: rgba(131, 83, 226, 0.1);
    color: #6d41d8;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
`;

export const SummaryHint = styled.span`
    color: #64748b;
    font-size: 12.5px;
    line-height: 1.4;
`;

export const DocList = styled.ul`
    list-style: none;
    margin: 4px 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

export const DocRow = styled.li`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 6px 0;
    color: #1f2937;
    font-size: 13px;
    line-height: 1.35;

    /* Hover-revealed row actions (the "Attach to case" affordance) fade in
     * on row hover, and stay visible while any descendant is focused so
     * keyboard users can still reach them. */
    &:hover [data-row-hover],
    &:focus-within [data-row-hover] {
        opacity: 1;
        pointer-events: auto;
        transform: translateX(0);
    }
`;

export const DocName = styled.span`
    color: #374151;
    font-weight: 600;
`;

/**
 * Doc name rendered as a link when the document is uploaded. Click opens the
 * shared `UrlDocumentPreview` Dialog.
 */
export const DocNameLink = styled.button`
    appearance: none;
    background: transparent;
    border: none;
    padding: 0;
    margin: 0;
    color: #6d41d8;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.35;
    cursor: pointer;
    text-align: left;
    text-decoration: underline;
    text-decoration-color: rgba(131, 83, 226, 0.36);
    text-underline-offset: 3px;
    transition:
        color 0.2s ease,
        text-decoration-color 0.2s ease;

    &:hover:not(:disabled) {
        color: #4c1d95;
        text-decoration-color: rgba(76, 29, 149, 0.6);
    }

    &:focus-visible {
        outline: none;
        text-decoration-color: #6d41d8;
        box-shadow: 0 0 0 2px rgba(131, 83, 226, 0.18);
        border-radius: 4px;
    }

    &:disabled {
        opacity: 0.7;
        cursor: progress;
    }
`;

/**
 * Hover-revealed "Attach to case" button on each provided row. Sits between
 * the name and the green Replace pill, fades in on row hover, and is small
 * enough to not crowd the right rail when the card is in its idle state.
 */
export const RowAttachButton = styled.button`
    appearance: none;
    background: rgba(131, 83, 226, 0.08);
    border: 1px solid rgba(131, 83, 226, 0.22);
    color: #6d41d8;
    padding: 3px 10px;
    border-radius: 999px;
    font-family: inherit;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    line-height: 1;
    cursor: pointer;
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
    transform: translateX(4px);
    /* Symmetric fade for hover-in / hover-out. 280ms is long enough that
     * the pill visibly fades out (rather than blinking off) when the
     * cursor leaves the row, while still feeling responsive on hover-in. */
    transition:
        opacity 0.28s ease,
        transform 0.28s ease,
        background 0.2s ease,
        border-color 0.2s ease,
        color 0.2s ease;

    &:hover:not(:disabled) {
        background: rgba(131, 83, 226, 0.16);
        border-color: rgba(131, 83, 226, 0.4);
        color: #4c1d95;
    }

    &:focus-visible {
        opacity: 1;
        pointer-events: auto;
        transform: translateX(0);
        outline: none;
        box-shadow: 0 0 0 3px rgba(131, 83, 226, 0.2);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

/**
 * Right-aligned action cluster on each doc row. Lays out the hover-revealed
 * "Attach to case" pill + the primary action (Upload / Replace) horizontally
 * so the row height stays constant whether the attach pill is visible or not.
 *
 * Uses `flex-wrap: wrap` so a long inline error wraps onto its own line
 * underneath without breaking up the inline pill cluster above it.
 */
export const DocAction = styled.div`
    display: inline-flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    min-width: 0;
`;

/**
 * Green pill button used to replace an uploaded document. Keeps a compact
 * row height while still exposing a real button for the file-replace flow.
 */
export const ReplaceBadgeButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    white-space: nowrap;
    background: rgba(34, 197, 94, 0.12);
    color: #15803d;
    border: 1px solid rgba(34, 197, 94, 0.28);
    cursor: pointer;
    line-height: 1;
    font-family: inherit;
    transition:
        transform 0.15s ease,
        background 0.2s ease,
        border-color 0.2s ease,
        box-shadow 0.2s ease,
        opacity 0.2s ease;

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        background: rgba(34, 197, 94, 0.18);
        border-color: rgba(34, 197, 94, 0.42);
        box-shadow: 0 6px 14px rgba(34, 197, 94, 0.18);
    }

    &:focus-visible {
        outline: none;
        box-shadow:
            0 0 0 3px rgba(34, 197, 94, 0.22),
            0 6px 14px rgba(34, 197, 94, 0.18);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

/**
 * Inline upload affordance shown in place of the "Missing" status. Uses the
 * same purple gradient as the primary action button across the app, sized
 * down to sit comfortably on the right edge of a doc row.
 */
export const UploadButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border: none;
    border-radius: 999px;
    background: linear-gradient(135deg, #8353e2 0%, #6d41d8 100%);
    color: #ffffff;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    line-height: 1;
    cursor: pointer;
    box-shadow: 0 6px 14px rgba(131, 83, 226, 0.22);
    transition:
        transform 0.15s ease,
        box-shadow 0.2s ease,
        opacity 0.2s ease;

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 10px 20px rgba(131, 83, 226, 0.3);
    }

    &:focus-visible {
        outline: none;
        box-shadow:
            0 0 0 3px rgba(131, 83, 226, 0.22),
            0 6px 14px rgba(131, 83, 226, 0.22);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

export const InlineError = styled.span`
    color: #b91c1c;
    font-size: 11px;
    line-height: 1.3;
    max-width: 160px;
    text-align: right;
`;

export const ErrorBanner = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 12px;
    background: rgba(239, 68, 68, 0.08);
    border: 1px solid rgba(239, 68, 68, 0.24);
    color: #991b1b;
    font-size: 12.5px;
    line-height: 1.4;
`;

/* -----------------------------------------------------------------------------
 * Skeleton shimmer used while documents are loading.
 * ---------------------------------------------------------------------------*/

export const SkeletonBlock = styled.div<{ width?: string; height?: string; radius?: string }>`
    width: ${({ width }) => width ?? '100%'};
    height: ${({ height }) => height ?? '14px'};
    border-radius: ${({ radius }) => radius ?? '6px'};
    background: linear-gradient(
        90deg,
        rgba(148, 163, 184, 0.12) 0%,
        rgba(148, 163, 184, 0.22) 50%,
        rgba(148, 163, 184, 0.12) 100%
    );
    background-size: 200% 100%;
    animation: initial-docs-shimmer 1.4s ease-in-out infinite;

    @keyframes initial-docs-shimmer {
        0% {
            background-position: 200% 0;
        }
        100% {
            background-position: -200% 0;
        }
    }
`;

export const SkeletonRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

/** Hidden file input — one per row, opened programmatically via ref. */
export const HiddenFileInput = styled.input`
    display: none;
`;

export const ModalInlineError = styled.span`
    color: #b91c1c;
    font-size: 12.5px;
    line-height: 1.4;
`;

/* -----------------------------------------------------------------------------
 * Case-picker modal shown when "Attach to case" is clicked on a provided
 * document.
 *
 * Visually mirrors the case dropdown menu used in QuickActions (each case is
 * a soft-bordered pill row with the program name), but without the briefcase
 * icon and laid out vertically inside the modal body.
 * ---------------------------------------------------------------------------*/

export const CaseList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 280px;
    overflow-y: auto;
`;

export const CaseListItem = styled.li`
    margin: 0;
    padding: 0;
`;

export const CaseOption = styled.button<{ selected?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding: 10px 14px;
    border-radius: 14px;
    border: 1px solid
        ${({ selected }) => (selected ? 'rgba(131, 83, 226, 0.36)' : 'rgba(148, 163, 184, 0.22)')};
    background: ${({ selected }) =>
        selected ? 'rgba(131, 83, 226, 0.08)' : 'rgba(255, 255, 255, 0.85)'};
    color: ${({ selected }) => (selected ? '#6d41d8' : '#1f2937')};
    font-size: 14px;
    font-weight: ${({ selected }) => (selected ? 600 : 500)};
    line-height: 1.3;
    text-align: left;
    cursor: pointer;
    transition:
        transform 0.15s ease,
        border-color 0.2s ease,
        background 0.2s ease,
        box-shadow 0.2s ease;

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        border-color: rgba(131, 83, 226, 0.28);
        background: rgba(131, 83, 226, 0.06);
        color: #6d41d8;
        box-shadow: 0 8px 18px rgba(15, 23, 42, 0.05);
    }

    &:focus-visible {
        outline: none;
        border-color: rgba(131, 83, 226, 0.45);
        box-shadow: 0 0 0 3px rgba(131, 83, 226, 0.18);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

export const CaseOptionLabel = styled.span`
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const CaseOptionCheck = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    color: #6d41d8;

    svg {
        width: 16px;
        height: 16px;
    }
`;

export const EmptyCases = styled.p`
    margin: 0;
    padding: 14px 16px;
    border-radius: 14px;
    background: rgba(148, 163, 184, 0.08);
    color: #64748b;
    font-size: 13px;
    font-style: italic;
    text-align: center;
`;
